import React, {
  useCallback,
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import clsx from 'clsx';
import Tag from 'core/components/Tag';
import ToolTip from 'core/components/Tooltip';
import {
  ClaimAcknowledgementStatusDTO,
  ClaimSummaryDTO,
} from 'dtos';
import Select from 'core/components/Select';
import ClaimAcknowledgementStatus from 'pages/Dashboard/pages/Billing/Charges/ClaimAcknowledgementStatus';
import CopyWrapper from 'core/components/CopyWrapper';
import {
  ClaimTagByType,
} from 'pages/Dashboard/pages/Billing/Charges/columns/ClaimTag';

type Parts = 'root' | 'select';

type Props = {
  value: Partial<ClaimSummaryDTO> | null;
  acknowledgementStatus?: ClaimAcknowledgementStatusDTO | null;
  options: Partial<ClaimSummaryDTO>[];
  onChange: (value: Partial<ClaimSummaryDTO> | Partial<ClaimSummaryDTO>[]) => void;
  showStatus?: boolean;
  classes?: Partial<Record<Parts, string>>;
};

type LabelProps = {
  claim: Partial<ClaimSummaryDTO> | null;
  hasSecondary?: boolean;
  isSecondary?: boolean;
  isDisplay?: boolean;
  showStatus?: boolean;
  acknowledgementStatus?: ClaimAcknowledgementStatusDTO | null;
};

export function ClaimLabel({
  claim,
  isSecondary = false,
  hasSecondary = false,
  isDisplay = false,
  showStatus = true,
  acknowledgementStatus = null,
}: LabelProps) {
  const iconTip = useMemo(() => {
    const message: string[] = [];
    if (isSecondary) {
      message.push('Secondary');
    } else if (hasSecondary) {
      message.push('Primary');
    }
    message.push('Claim');

    return message.join(' ');
  }, [isSecondary, hasSecondary]);

  return (
    <div className={clsx('flex !h-4 w-full items-center justify-between gap-1', {
      'ml-2': isSecondary && !isDisplay,
    })}
    >
      <div className={clsx(
        'flex flex-1 truncate items-center gap-1 text-xs text-gray-500 font-semibold',
        { '!ml-5': isDisplay },
      )}
      >
        <ToolTip
          title={iconTip}
          hide={(!isSecondary && !hasSecondary) || !isDisplay}
        >
          <span>
            {hasSecondary && (<ClaimTagByType hasSecondary />)}
            {isSecondary && (<ClaimTagByType isSecondary />)}
          </span>
        </ToolTip>
        {claim?.externalId}
      </div>
      {showStatus && (
        <Tag
          tagName={claim?.status ?? '--'}
          className="!font-medium !py-0 !px-3 max-h-[20px]"
          defaultColor="!bg-brown-100 !text-brown-700"
        />
      )}
      {!isNil(acknowledgementStatus) && (
        <ClaimAcknowledgementStatus {...acknowledgementStatus as ClaimAcknowledgementStatusDTO} />
      )}
    </div>
  );
}

export default function ClaimSelect({
  value,
  options,
  acknowledgementStatus,
  showStatus,
  classes = {},
  ...props
}: Props) {
  const keyedSecondaryClaims = keyBy(
    options.filter(({ referencedPrimaryClaimId }) => !isNil(referencedPrimaryClaimId)),
    'referencedPrimaryClaimId',
  );

  const getLabel = useCallback((option: Partial<ClaimSummaryDTO>) => (
    <ClaimLabel
      claim={option}
      showStatus={showStatus}
      isSecondary={!isNil(option?.referencedPrimaryClaimId)}
      hasSecondary={!isNil(keyedSecondaryClaims[option?.claimId ?? 0])}
    />
  ), [keyedSecondaryClaims]);

  return (
    <div className={clsx('relative w-full claim-select-wrapper', classes.root)}>
      <CopyWrapper
        text={value?.externalId ?? ''}
        successMessage={`Claim ID: ${value?.externalId ?? ''} was copied to clipboard.`}
        tooltip="Copy Claim ID to Clipboard"
        className="!p-0.5 !absolute z-10 top-1/2 -translate-y-1/2 left-1"
      >
        <Select<Partial<ClaimSummaryDTO>>
          {...props}
          options={options}
          disabled={options.length === 1}
          value={value}
          getValue={(claim) => claim?.claimId ?? 0}
          getLabel={getLabel}
          renderValue={() => (
            <ClaimLabel
              claim={value ?? {}}
              hasSecondary={!isNil(keyedSecondaryClaims[value?.claimId ?? 0])}
              isSecondary={!isNil(value?.referencedPrimaryClaimId)}
              isDisplay
              showStatus={showStatus}
              acknowledgementStatus={acknowledgementStatus}
            />
          )}
          muiClasses={{
            select: clsx('!py-0.5 !pl-1', {
              '!pr-1.5': options.length === 1,
            }),
            icon: clsx({ '!hidden': options.length === 1 }),
          }}
          selectClassName={classes.select}
        />
      </CopyWrapper>
    </div>
  );
}
